import fs from "fs";
import path from "path";
import { loadConfig } from "../../config";
import { getMCPClient } from "../../mcp/mcpManager";

export async function runMcpConfig(args: string[]) {
  const action = args[0] || "show";
  const cwd = process.cwd();
  const mcpConfigPath = path.join(cwd, ".metis", "mcp-servers.json");

  switch (action) {
    case "show":
    case "list":
      await showMCPServers();
      break;
    case "add":
      await addMCPServer(args.slice(1), mcpConfigPath);
      break;
    case "remove":
    case "rm":
      await removeMCPServer(args.slice(1), mcpConfigPath);
      break;
    case "connect":
      await connectMCPServer(args.slice(1));
      break;
    case "disconnect":
      await disconnectMCPServer(args.slice(1));
      break;
    case "test":
      await testMCPServer(args.slice(1));
      break;
    default:
      console.log(`
Usage: metiscode mcp [action]

Actions:
  show/list                    Show configured MCP servers
  add <id> <config>           Add MCP server configuration  
  remove/rm <id>              Remove MCP server configuration
  connect <id>                Connect to configured MCP server
  disconnect <id>             Disconnect from MCP server
  test <id>                   Test MCP server connection

Configuration Examples:
  # Add stdio server
  metiscode mcp add myserver '{
    "name": "My Server",
    "version": "1.0.0", 
    "description": "My MCP server",
    "capabilities": {"tools": true, "resources": true},
    "transport": {
      "type": "stdio",
      "command": "node",
      "args": ["server.js"]
    }
  }'
  
  # Add websocket server  
  metiscode mcp add wsserver '{
    "name": "WebSocket Server",
    "version": "1.0.0",
    "description": "WebSocket MCP server", 
    "capabilities": {"tools": true},
    "transport": {
      "type": "websocket",
      "url": "ws://localhost:8080"
    }
  }'
`);
  }
}

async function showMCPServers() {
  try {
    const client = getMCPClient();
    const servers = client.getConnectedServers();
    const configPath = path.join(process.cwd(), ".metis", "mcp-servers.json");
    
    // Load stored configurations
    let storedConfigs: any = {};
    if (fs.existsSync(configPath)) {
      try {
        storedConfigs = JSON.parse(fs.readFileSync(configPath, 'utf8'));
      } catch (error) {
        console.log("Warning: Could not parse MCP servers config");
      }
    }
    
    console.log("MCP Servers:");
    
    if (Object.keys(storedConfigs).length === 0) {
      console.log("  No MCP servers configured");
      return;
    }
    
    for (const [id, config] of Object.entries(storedConfigs)) {
      const connectedServer = servers.find(s => s.id === id);
      const status = connectedServer ? connectedServer.status : "not connected";
      const configData = config as any;
      
      console.log(`  ${id}:`);
      console.log(`    Name: ${configData.serverConfig?.name || 'Unknown'}`);
      console.log(`    Version: ${configData.serverConfig?.version || 'Unknown'}`);
      console.log(`    Transport: ${configData.transport?.type || 'Unknown'}`);
      console.log(`    Status: ${status}`);
      
      if (connectedServer?.lastError) {
        console.log(`    Last Error: ${connectedServer.lastError}`);
      }
      
      const capabilities = configData.serverConfig?.capabilities;
      if (capabilities) {
        const caps = [];
        if (capabilities.resources) caps.push("resources");
        if (capabilities.tools) caps.push("tools");
        if (capabilities.prompts) caps.push("prompts");
        if (capabilities.logging) caps.push("logging");
        console.log(`    Capabilities: ${caps.join(", ") || "none"}`);
      }
      console.log("");
    }
  } catch (error: any) {
    console.error("Error showing MCP servers:", error.message);
  }
}

async function addMCPServer(args: string[], configPath: string) {
  if (args.length < 2) {
    console.log("Error: Please specify server ID and configuration");
    console.log("Usage: metiscode mcp add <id> <config-json>");
    return;
  }
  
  const [serverId, configJson] = args;
  
  try {
    const serverConfig = JSON.parse(configJson);
    
    // Validate required fields
    if (!serverConfig.name || !serverConfig.version || !serverConfig.description) {
      console.log("Error: Server config must include name, version, and description");
      return;
    }
    
    if (!serverConfig.capabilities) {
      console.log("Error: Server config must include capabilities object");
      return;
    }
    
    if (!serverConfig.transport || !serverConfig.transport.type) {
      console.log("Error: Server config must include transport configuration");
      return;
    }
    
    // Validate transport type
    const validTransports = ["stdio", "websocket", "http"];
    if (!validTransports.includes(serverConfig.transport.type)) {
      console.log(`Error: Transport type must be one of: ${validTransports.join(", ")}`);
      return;
    }
    
    // Ensure .metis directory exists
    const metisDir = path.dirname(configPath);
    if (!fs.existsSync(metisDir)) {
      fs.mkdirSync(metisDir, { recursive: true });
    }
    
    // Load existing configurations
    let configs: any = {};
    if (fs.existsSync(configPath)) {
      try {
        configs = JSON.parse(fs.readFileSync(configPath, 'utf8'));
      } catch (error) {
        console.log("Warning: Could not parse existing MCP config, creating new one");
      }
    }
    
    // Add the new server configuration
    configs[serverId] = {
      serverConfig: {
        name: serverConfig.name,
        version: serverConfig.version,
        description: serverConfig.description,
        author: serverConfig.author,
        homepage: serverConfig.homepage,
        capabilities: serverConfig.capabilities
      },
      transport: serverConfig.transport
    };
    
    // Save configurations
    fs.writeFileSync(configPath, JSON.stringify(configs, null, 2) + "\n");
    
    console.log(`MCP server '${serverId}' added successfully`);
    console.log(`Configuration saved to: ${configPath}`);
    console.log(`\nTo connect: metiscode mcp connect ${serverId}`);
    
  } catch (error: any) {
    if (error.name === "SyntaxError") {
      console.log("Error: Invalid JSON configuration");
      console.log("Make sure to properly escape quotes in JSON");
    } else {
      console.log(`Error adding MCP server: ${error.message}`);
    }
  }
}

async function removeMCPServer(args: string[], configPath: string) {
  if (args.length < 1) {
    console.log("Error: Please specify server ID");
    console.log("Usage: metiscode mcp remove <id>");
    return;
  }
  
  const serverId = args[0];
  
  try {
    if (!fs.existsSync(configPath)) {
      console.log("No MCP servers configured");
      return;
    }
    
    const configs = JSON.parse(fs.readFileSync(configPath, 'utf8'));
    
    if (!configs[serverId]) {
      console.log(`MCP server '${serverId}' not found`);
      return;
    }
    
    // Disconnect if connected
    const client = getMCPClient();
    try {
      await client.unregisterServer(serverId);
    } catch (error) {
      // Server might not be connected, that's okay
    }
    
    // Remove from configuration
    delete configs[serverId];
    
    // Save updated configurations
    fs.writeFileSync(configPath, JSON.stringify(configs, null, 2) + "\n");
    
    console.log(`MCP server '${serverId}' removed successfully`);
    
  } catch (error: any) {
    console.log(`Error removing MCP server: ${error.message}`);
  }
}

async function connectMCPServer(args: string[]) {
  if (args.length < 1) {
    console.log("Error: Please specify server ID");
    console.log("Usage: metiscode mcp connect <id>");
    return;
  }
  
  const serverId = args[0];
  const configPath = path.join(process.cwd(), ".metis", "mcp-servers.json");
  
  try {
    if (!fs.existsSync(configPath)) {
      console.log("No MCP servers configured");
      console.log("Use 'metiscode mcp add' to add a server first");
      return;
    }
    
    const configs = JSON.parse(fs.readFileSync(configPath, 'utf8'));
    const serverConfig = configs[serverId];
    
    if (!serverConfig) {
      console.log(`MCP server '${serverId}' not found in configuration`);
      console.log("Use 'metiscode mcp list' to see available servers");
      return;
    }
    
    const client = getMCPClient();
    
    // Check if already connected
    const existingServer = client.getServerById(serverId);
    if (existingServer && existingServer.status === "connected") {
      console.log(`MCP server '${serverId}' is already connected`);
      return;
    }
    
    console.log(`Connecting to MCP server '${serverId}'...`);
    
    const { createTransport } = await import("../../mcp/transport");
    const connection = createTransport(serverConfig.transport);
    
    await connection.connect();
    await client.registerServer(serverId, serverConfig.serverConfig, connection);
    
    console.log(`Successfully connected to MCP server '${serverId}'`);
    console.log(`Server: ${serverConfig.serverConfig.name} v${serverConfig.serverConfig.version}`);
    
  } catch (error: any) {
    console.log(`Error connecting to MCP server '${serverId}': ${error.message}`);
  }
}

async function disconnectMCPServer(args: string[]) {
  if (args.length < 1) {
    console.log("Error: Please specify server ID");
    console.log("Usage: metiscode mcp disconnect <id>");
    return;
  }
  
  const serverId = args[0];
  
  try {
    const client = getMCPClient();
    await client.unregisterServer(serverId);
    console.log(`Disconnected from MCP server '${serverId}'`);
  } catch (error: any) {
    console.log(`Error disconnecting from MCP server '${serverId}': ${error.message}`);
  }
}

async function testMCPServer(args: string[]) {
  if (args.length < 1) {
    console.log("Error: Please specify server ID");
    console.log("Usage: metiscode mcp test <id>");
    return;
  }
  
  const serverId = args[0];
  
  try {
    const client = getMCPClient();
    const server = client.getServerById(serverId);
    
    if (!server) {
      console.log(`MCP server '${serverId}' not found`);
      console.log("Use 'metiscode mcp connect <id>' to connect first");
      return;
    }
    
    if (server.status !== "connected") {
      console.log(`MCP server '${serverId}' is not connected (status: ${server.status})`);
      return;
    }
    
    console.log(`Testing MCP server '${serverId}'...`);
    
    // Test capabilities
    const capabilities = server.config.capabilities;
    console.log(`\nCapabilities:`);
    
    if (capabilities.resources) {
      try {
        const resources = await client.listResources(serverId);
        console.log(`  Resources: ${resources.length} available`);
      } catch (error: any) {
        console.log(`  Resources: Error - ${error.message}`);
      }
    } else {
      console.log(`  Resources: Not supported`);
    }
    
    if (capabilities.tools) {
      try {
        const tools = await client.listTools(serverId);
        console.log(`  Tools: ${tools.length} available`);
        if (tools.length > 0) {
          console.log(`    Tool names: ${tools.map(t => t.name).join(", ")}`);
        }
      } catch (error: any) {
        console.log(`  Tools: Error - ${error.message}`);
      }
    } else {
      console.log(`  Tools: Not supported`);
    }
    
    if (capabilities.prompts) {
      try {
        const prompts = await client.listPrompts(serverId);
        console.log(`  Prompts: ${prompts.length} available`);
      } catch (error: any) {
        console.log(`  Prompts: Error - ${error.message}`);
      }
    } else {
      console.log(`  Prompts: Not supported`);
    }
    
    console.log(`\nMCP server '${serverId}' test completed successfully`);
    
  } catch (error: any) {
    console.log(`Error testing MCP server '${serverId}': ${error.message}`);
  }
}